import { useState, useEffect, lazy, Suspense, Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, GraduationCap } from "lucide-react";
import { EduAISEOHead } from "@/components/seo/EduAISEOHead";
import { Footer } from "@/components/layout/Footer";

import ContactFormSection from "@/components/ContactFormSection";

const AIChatbot = lazy(() => import("@/components/AIChatbot"));

const peterProfile = "/peter-profile.webp";
const peterProfileLarge = "/peter-photo.webp";

import logoErb from "@/assets/partners/erb-transparent.png";
import logoHosAsset from "@/assets/partners/hornosarisske-osvetove-stredisko.png.asset.json";
const logoHos = logoHosAsset.url;
import logoKtc from "@/assets/partners/ktc-transparent.png";
import logoEdulabAsset from "@/assets/partners/edulab-logo.png.asset.json";
const logoEdulab = logoEdulabAsset.url;
import logoEakademy from "@/assets/partners/eakademy-transparent.png";
import logoCenef from "@/assets/partners/cenef-transparent.png";
import logoAvAsset from "@/assets/partners/av-activities.png.asset.json";
const logoAv = logoAvAsset.url;

type Partner = { name: string; src: string; label?: boolean };

const partners: Partner[] = [
  { name: "Mesto Bardejov", src: logoErb },
  { name: "Hornošarišské osvetové stredisko", src: logoHos },
  { name: "KTC Bardejov", src: logoKtc },
  { name: "EDULAB", src: logoEdulab },
  { name: "eAkadémia", src: logoEakademy },
  { name: "CENEF", src: logoCenef },
  { name: "AV Activities", src: logoAv, label: true },
];

/* ---------------- Reveal helper ---------------- */
const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const Reveal = ({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={{
      hidden: { opacity: 0, y: 24 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] } },
    }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------------- Section header (eyebrow + number) ---------------- */
const SectionLabel = ({ num, label }: { num: string; label: string }) => (
  <div className="flex items-baseline gap-4 mb-8 md:mb-14">
    <span className="font-heading text-2xl md:text-3xl text-accent">{num}</span>
    <span className="h-px flex-grow bg-foreground/15" aria-hidden="true" />
    <span className="type-eyebrow text-foreground/70">{label}</span>
  </div>
);


/* ---------------- Data ---------------- */
const nav = [
  { name: "Pre koho", id: "pre-koho" },
  { name: "Služby", id: "sluzby" },
  { name: "Výsledky", id: "vysledky" },
  { name: "O mne", id: "o-mne" },
  { name: "Kontakt", id: "kontakt" },
];

const services = [
  {
    title: "Webinár pre tím",
    desc: "90-minútové online školenie so základmi AI, praktickými ukážkami a priestorom na otázky. Vhodné ako spoločný úvod do AI pre celý kolektív.",
    cta: "Chcem webinár pre tím",
  },
  {
    title: "Prezenčný workshop",
    desc: "Praktické štvorhodinové školenie priamo vo vašej organizácii. Účastníci si postupy vyskúšajú na vlastných pracovných úlohách.",
    cta: "Chcem workshop u nás",
  },
  {
    title: "AI riešenie na mieru",
    desc: "Analýza pracovných procesov, návrh vhodných AI nástrojov a zavedenie konkrétnych šablón, postupov alebo automatizácií.",
    cta: "Prebrať riešenie na mieru",
  },
];

/**
 * Skutočné referencie – dopĺňať iba so súhlasom autora.
 * logo: nepovinné (cesta k obrázku alebo URL loga organizácie).
 */
const references: {
  quote: string;
  author: string;
  role: string;
  organization: string;
  logo?: string;
}[] = [];




/* ---------------- Home ---------------- */
export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedService, setSelectedService] = useState<string>("");


  const [activeSection, setActiveSection] = useState<string>("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      let current = "";
      for (const n of nav) {
        const el = document.getElementById(n.id);
        if (el && el.getBoundingClientRect().top - 90 <= 0) current = n.id;
      }
      setActiveSection(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMenuOpen]);

  const scrollTo = (id: string) => {
    setIsMenuOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: "smooth" });
    }, 80);
  };

  const scrollToContactWithService = (serviceTitle: string) => {
    setSelectedService(serviceTitle);
    scrollTo("kontakt");
  };



  return (
    <div className="min-h-screen bg-background text-foreground antialiased overflow-x-hidden">
      <EduAISEOHead
        title="Peter Tej – AI mentor a lektor pre organizácie"
        description="Osobná stránka Petra Teja. Praktické AI workshopy, konzultácie a mentoring pre školy, verejnú správu a firmy na Slovensku."
      />

      {/* ============ HEADER ============ */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || isMenuOpen ? "bg-background/95 backdrop-blur-md border-b border-foreground/8" : "bg-background/90 backdrop-blur-sm"
        }`}
      >
        <div className="site-header-inner">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setIsMenuOpen(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="site-brand font-heading text-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
          >
            Peter Tej
          </a>

          <nav className="site-desktop-nav" aria-label="Hlavná navigácia">
            {nav.map((n) => {
              const isActive = activeSection === n.id;
              return (
                <button
                  key={n.id}
                  onClick={() => scrollTo(n.id)}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative text-[13px] tracking-wide transition-colors py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-background rounded-sm ${
                    isActive ? "text-foreground" : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {n.name}
                  <span
                    className={`pointer-events-none absolute -bottom-0.5 left-0 h-px bg-accent transition-all duration-300 ${
                      isActive ? "w-full opacity-100" : "w-0 opacity-0"
                    }`}
                  />
                </button>
              );
            })}
            <a href="/ai-mastery-program" className="masterclass-nav-link text-[13px] min-h-11">
              <GraduationCap className="size-4 text-accent" aria-hidden="true" /> MasterClass
            </a>
            <a href="/biznis-promptpack-2026" className="masterclass-nav-link text-[13px] min-h-11">
              PromptPack <span className="font-semibold text-accent">9,99 €</span>
            </a>
            <a
              href="/zadarmo"
              className="text-[13px] text-foreground/70 hover:text-foreground transition-colors py-1"
            >
              Zadarmo
            </a>
            <a
              href="mailto:info@petertej.sk"
              className="site-header-email text-[13px] text-foreground/70 hover:text-foreground transition-colors py-1"
            >
              info@petertej.sk
            </a>
            <button
              onClick={() => scrollTo("kontakt")}
              className="group ml-2 btn-base btn-primary min-h-[44px] px-5 text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Prebrať spoluprácu
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
            </button>

          </nav>

          <button
            onClick={() => setIsMenuOpen((v) => !v)}
            className="site-menu-toggle inline-flex items-center justify-center w-11 h-11 -mr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-md"
            aria-label={isMenuOpen ? "Zavrieť menu" : "Otvoriť menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
          </button>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              id="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="site-mobile-menu bg-background border-t border-foreground/8"
            >
              <nav className="container-page py-4 flex flex-col" aria-label="Mobilná navigácia">
                {nav.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => scrollTo(n.id)}
                    aria-current={activeSection === n.id ? "true" : undefined}
                    className={`text-left font-heading text-xl min-h-[44px] flex items-center border-b border-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm ${
                      activeSection === n.id ? "text-accent" : ""
                    }`}
                  >
                    {n.name}
                  </button>
                ))}
                <button
                  onClick={() => scrollTo("kontakt")}
                  className="mt-4 w-full btn-base btn-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >

                  Prebrať spoluprácu
                  <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                </button>
                <a href="/ai-mastery-program" onClick={() => setIsMenuOpen(false)} className="masterclass-nav-link mt-4 min-h-12 justify-between text-base"><span className="flex items-center gap-3"><GraduationCap className="size-5 text-accent" aria-hidden="true" />MasterClass</span><ArrowUpRight className="size-4 text-accent" aria-hidden="true" /></a>
                <a href="/biznis-promptpack-2026" onClick={() => setIsMenuOpen(false)} className="masterclass-nav-link mt-2 min-h-12 justify-between text-base"><span>PromptPack 2026</span><span className="text-sm font-semibold text-accent">9,99 €</span></a>
                <div className="mt-6 -mx-5 px-5 py-4 bg-secondary border-t border-foreground/10">
                  <p className="type-eyebrow text-foreground/60 mb-2">Ďalšie stránky</p>
                  {[
                    ["/ai-test", "AI test zadarmo"],
                    ["/zadarmo", "Materiály zadarmo"],
                  ].map(([href, label]) => (
                    <a
                      key={href}
                      href={href}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-sm tracking-wide text-foreground/80 min-h-[44px] flex items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                    >
                      {label}
                      <ArrowUpRight className="w-4 h-4 text-accent" aria-hidden="true" />
                    </a>
                  ))}
                  <a href="mailto:info@petertej.sk" className="mt-2 text-sm text-foreground/70 min-h-[44px] flex items-center underline underline-offset-4">
                    info@petertej.sk
                  </a>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main id="main-content">
      {/* ============ HERO ============ */}
      <section className="section-y pt-28 lg:pt-32 flex items-center">
        <div className="container-page grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text (first on mobile) */}
          <div className="lg:col-span-6 order-1">
            <Reveal>
              <p className="type-eyebrow text-foreground/70 mb-6 md:mb-10">
                Praktická AI pre školy, samosprávy a firmy
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="type-h1 text-balance">
                Pomáham tímom zaviesť AI do každodennej práce.
              </h1>
            </Reveal>

            <Reveal delay={0.25}>
              <p className="mt-6 md:mt-8 measure type-body text-foreground/70">
                Školenia, konzultácie a konkrétne AI riešenia, ktoré šetria čas učiteľom, vedeniu, úradom aj firmám. Zrozumiteľne a bez technického žargónu.
              </p>
            </Reveal>

            <Reveal delay={0.35}>
              <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
                <button
                  onClick={() => scrollTo("kontakt")}
                  className="group btn-base btn-primary w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  Prebrať spoluprácu
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => scrollTo("sluzby")}
                  className="btn-base btn-secondary w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  Pozrieť služby
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.45}>
              <div className="mt-10 md:mt-14 type-body text-foreground/70">
                <span className="block sm:inline">
                  <strong className="font-semibold text-foreground">2 040+</strong> vyškolených ľudí
                </span>
                <span className="hidden sm:inline mx-3 text-foreground/40" aria-hidden="true">·</span>
                <span className="block sm:inline mt-1.5 sm:mt-0">
                  <strong className="font-semibold text-foreground">115</strong> realizovaných podujatí
                </span>
                <span className="hidden sm:inline mx-3 text-foreground/40" aria-hidden="true">·</span>
                <span className="block sm:inline mt-1.5 sm:mt-0">
                  Google AI certifikácia
                </span>
              </div>
            </Reveal>
          </div>


          {/* Photo (after text on mobile, right on desktop) */}
          <div className="lg:col-span-6 order-2">
            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-[280px] sm:max-w-[320px] md:max-w-[340px] lg:max-w-[480px] mx-auto"
            >
              <div aria-hidden="true" className="hidden lg:block absolute inset-0 translate-x-5 translate-y-5 border border-accent/60 rounded-[var(--radius)]" />
              <div className="relative aspect-[4/5] max-h-[60vh] lg:max-h-none overflow-hidden rounded-[var(--radius)] bg-secondary shadow-[0_20px_50px_-25px_hsl(0_0%_0%/0.35)]">
                <img
                  src={peterProfileLarge}
                  srcSet={`${peterProfile} 400w, ${peterProfileLarge} 800w`}
                  sizes="(max-width: 1024px) 340px, 480px"
                  alt="Peter Tej, lektor a konzultant pre praktické využitie umelej inteligencie."
                  className="w-full h-full object-cover object-top"
                  width={520}
                  height={650}
                  {...({ fetchpriority: "high" } as Record<string, string>)}
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ 01. PRE KOHO ============ */}
      <section id="pre-koho" className="section-y section-divided">
        <div className="container-page">

          <Reveal>
            <SectionLabel num="01" label="Pre koho" />
          </Reveal>

          <Reveal>
            <h2 className="type-h2 max-w-4xl">
              Pomáham organizáciám pracovať s AI prakticky a bezpečne.
            </h2>
          </Reveal>

          <div className="mt-10 md:mt-14 max-w-4xl">
            {[
              [
                "Školy a vedenie",
                "Menej administratívy, rýchlejšia príprava dokumentov a jasné pravidlá používania AI v škole.",
              ],
              [
                "Učitelia",
                "Praktické postupy na prípravu hodín, tvorbu materiálov, spätnú väzbu a prácu so žiakmi.",
              ],
              [
                "Samosprávy a verejná správa",
                "Rýchlejšia práca s dokumentmi, komunikáciou s občanmi a opakovanými kancelárskymi procesmi.",
              ],
              [
                "Firmy a pracovné tímy",
                "AI nástroje, šablóny a automatizácie, ktoré znižujú množstvo rutinnej práce.",
              ],
            ].map(([title, desc], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="grid md:grid-cols-[200px_1fr] lg:grid-cols-[260px_1fr] gap-2 md:gap-10 lg:gap-12 py-6 md:py-7 border-b border-foreground/10 first:border-t">
                  <h3 className="type-h3">{title}</h3>
                  <p className="type-body text-foreground/70 measure">{desc}</p>
                </div>
              </Reveal>

            ))}
          </div>
        </div>
      </section>

      {/* ============ 02. SLUŽBY ============ */}
      <section id="sluzby" className="section-y section-divided bg-secondary">
        <div className="container-page">
          <Reveal>
            <SectionLabel num="02" label="Služby" />
          </Reveal>
          <Reveal>
            <h2 className="type-h2 max-w-3xl mb-10 md:mb-14">
              Tri spôsoby, ako môžeme začať spolupracovať.
            </h2>
          </Reveal>

          <div>
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="group py-6 md:py-8 border-t border-foreground/15 flex flex-col md:flex-row md:items-start gap-4 md:gap-12">
                  <span className="font-heading text-sm text-accent tabular-nums w-10 shrink-0 pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-grow">
                    <h3 className="type-h3">{s.title}</h3>
                    <p className="mt-2 type-body text-foreground/70 measure">{s.desc}</p>
                  </div>
                  <button
                    onClick={() => scrollToContactWithService(s.title)}
                    className="shrink-0 btn-base btn-primary w-full md:w-auto md:self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                  >
                    {s.cta}
                    <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>

                </div>
              </Reveal>
            ))}
            <div className="border-t border-foreground/15" />
          </div>
        </div>
      </section>

      {/* ============ 03. PRIEBEH SPOLUPRÁCE ============ */}
      <section id="priebeh-spoluprace" className="section-y section-divided">
        <div className="container-page">
          <Reveal>
            <SectionLabel num="03" label="Priebeh spolupráce" />
          </Reveal>

          <Reveal>
            <h2 className="type-h2 max-w-3xl mb-12 md:mb-16">
              Od potrieb organizácie ku konkrétnemu riešeniu.
            </h2>

          </Reveal>

          <div className="grid lg:grid-cols-3 gap-0 lg:gap-12">
            {[
              [
                "Zistíme potreby",
                "Spoločne pomenujeme pracovné úlohy, problémy a procesy, pri ktorých môže AI priniesť reálnu úsporu času.",
              ],
              [
                "Navrhnem vhodný postup",
                "Vyberiem formu školenia, nástroje a praktické riešenia podľa skúseností účastníkov a cieľov organizácie.",
              ],
              [
                "Riešenie použijete v praxi",
                "Účastníci získajú konkrétne postupy, šablóny a materiály, ktoré môžu začať používať pri svojej práci.",
              ],
            ].map(([title, desc], i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div className={`py-6 lg:py-0 border-t lg:border-t-0 border-foreground/15 grid sm:grid-cols-[48px_1fr] lg:block gap-x-6 ${i > 0 ? "lg:border-l lg:pl-8" : ""}`}>
                  <span className="font-heading text-sm text-accent tabular-nums block mb-4">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="type-h3 mb-3">{title}</h3>
                    <p className="type-body text-foreground/70 measure">{desc}</p>
                  </div>

                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

{/* ============ 04. VÝSLEDKY ============ */}
      <section id="vysledky" className="section-y section-divided bg-secondary">
        <div className="container-page">
          <Reveal>
            <SectionLabel num="04" label="Výsledky" />
          </Reveal>

          <Reveal>
            <h2 className="type-h2 max-w-3xl mb-12 md:mb-16">
              Výsledky a skúsenosti <em className="not-italic text-accent">z reálnej praxe</em>.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-y-10 md:gap-x-16">
            {[
              ["2 040+", "vyškolených ľudí"],
              ["115", "realizovaných školení a vzdelávacích podujatí"],
              ["Desiatky organizácií", "zo školstva, verejnej správy a firemného prostredia po celom Slovensku"],
            ].map(([n, d], i) => (
              <Reveal key={n} delay={i * 0.1}>
                <p className="font-heading text-[40px] md:text-[56px] leading-[1.05] tracking-[-0.03em]">{n}</p>
                <p className="mt-3 type-body text-foreground/70">{d}</p>
              </Reveal>

            ))}
          </div>

          <div className="mt-16 md:mt-20 border-t border-foreground/15 pt-8">
            <p className="type-eyebrow text-foreground/70 mb-6">Spolupracoval som s organizáciami</p>
            <ul className="flex flex-wrap items-center justify-center gap-x-10">
              {partners.map((p, i) => (
                <Fragment key={p.name}>
                  {i === 4 && (
                    <li aria-hidden="true" className="hidden h-0 basis-full sm:block xl:hidden" />
                  )}
                  <li className="my-4 flex h-16 items-center justify-center gap-3">
                    <img
                      src={p.src}
                      alt={p.label ? "" : p.name}
                      aria-hidden={p.label ? true : undefined}
                      loading="lazy"
                      decoding="async"
                      className={
                        p.label
                          ? "h-14 w-auto object-contain grayscale opacity-60 mix-blend-multiply hover:grayscale-0 hover:opacity-100 transition duration-300"
                          : "max-h-12 max-w-[150px] w-auto object-contain grayscale opacity-60 mix-blend-multiply hover:grayscale-0 hover:opacity-100 transition duration-300"
                      }
                    />
                    {p.label && (
                      <span className="whitespace-nowrap text-sm font-medium tracking-wide text-foreground/60 transition duration-300 hover:text-foreground">
                        {p.name}
                      </span>
                    )}
                  </li>
                </Fragment>
              ))}
            </ul>
          </div>



          {references.length > 0 && (
            <div className="mt-16 md:mt-24 grid md:grid-cols-2 gap-10 md:gap-16">
              {references.map((r, i) => (
                <Reveal key={r.author} delay={i * 0.1}>
                  <div className="border-t border-foreground/15 pt-6">
                    <p className="font-heading text-lg md:text-xl leading-[1.5]">„{r.quote}“</p>
                    <div className="mt-5 flex items-center gap-4">
                      {r.logo && (
                        <img src={r.logo} alt={r.organization} className="h-9 w-auto object-contain" loading="lazy" />
                      )}
                      <div>
                        <p className="text-sm font-medium">{r.author}</p>
                        <p className="text-sm text-foreground/70">{r.role}, {r.organization}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

        </div>
      </section>

      {/* ============ 05. O MNE ============ */}
      <section id="o-mne" className="section-y section-divided">
        <div className="container-page">
          <Reveal>
            <SectionLabel num="05" label="O mne" />
          </Reveal>

          <div>
            <div>
              <Reveal>
                <h2 className="type-h2 max-w-4xl">
                  Som Peter Tej — lektor, mentor a konzultant pre praktické využitie umelej inteligencie.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 md:mt-10 type-body text-foreground/70 measure">
                  Viac ako 15 rokov pôsobím v školstve. Prepájam skúsenosti učiteľa, mentora a vedenia vzdelávacích programov s praktickým využitím moderných technológií.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-4 md:mt-5 type-body text-foreground/70 measure">
                  Školám, samosprávam a firmám pomáham zavádzať AI tak, aby zjednodušovala konkrétnu prácu, šetrila čas a bola zrozumiteľná aj pre ľudí bez technického vzdelania.
                </p>
              </Reveal>

              <Reveal delay={0.35}>
                <div className="mt-10 md:mt-14 grid md:grid-cols-2 gap-x-12 gap-y-6 max-w-4xl">
                  {[
                    ["Prax namiesto teórie", "Každé školenie stavím na úlohách, ktoré účastníci riešia v bežnej práci. Odchádzajú s postupmi, ktoré použijú hneď na druhý deň."],
                    ["Bezpečné a zodpovedné používanie", "Vysvetľujem, aké údaje do AI nepatria, ako chrániť osobné údaje podľa GDPR a ako výsledky AI vždy overiť."],
                    ["Prispôsobené vášmu tímu", "Obsah a tempo nastavím podľa skúseností ľudí – od úplných začiatočníkov až po tých, ktorí už AI bežne používajú."],
                    ["Skúsenosť učiteľa a lektora", "Viac ako 15 rokov v školstve mi pomáha vysvetľovať technológie zrozumiteľne, pokojne a bez zbytočného žargónu."],
                  ].map(([t, d]) => (
                    <div key={t} className="border-t border-foreground/15 pt-5">
                      <h3 className="type-h3 mb-2">{t}</h3>
                      <p className="type-body text-foreground/70">{d}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ LEAD MAGNET ============ */}
      <section aria-labelledby="lead-magnet-title" className="section-divided">
        <div className="container-page py-14 md:py-20">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center border-l-2 border-accent pl-6 md:pl-10">
            <div className="lg:col-span-7">
              <p className="type-eyebrow text-foreground/70 mb-4">Bezplatný prvý krok</p>
              <h2 id="lead-magnet-title" className="type-h2">
                Ešte nie ste pripravení na školenie?
              </h2>
              <p className="mt-4 type-body text-foreground/70 measure">
                Otestujte si pripravenosť svojej školy alebo organizácie na AI zadarmo, alebo si stiahnite praktické materiály a návody.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:justify-end">
              <a href="/ai-test" className="group btn-base btn-primary w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">
                Spustiť AI test zadarmo
                <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <a href="/zadarmo" className="btn-base btn-secondary w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">
                Materiály zadarmo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 06. KONTAKT ============ */}
      <section id="kontakt" className="section-y section-divided bg-secondary">
        <div className="container-page">
          <Reveal>
            <SectionLabel num="06" label="Kontakt" />
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <Reveal>
                <h2 className="type-h2 mb-5">
                  Čo chcete pomocou AI zjednodušiť?
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="type-body text-foreground/70 measure mb-6">
                  Napíšte mi, aký typ organizácie zastupujete a ktorá pracovná činnosť vám berie najviac času. Do 24 hodín navrhnem vhodný ďalší krok.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <a
                  href="mailto:info@petertej.sk"
                  className="type-body min-h-[44px] items-center text-foreground hover:text-accent underline underline-offset-4 transition-colors inline-block break-all"
                >

                  info@petertej.sk
                </a>
              </Reveal>
            </div>
            <div className="lg:col-span-7 w-full">
              <Reveal delay={0.15}>
                <ContactFormSection prefilledService={selectedService} />
              </Reveal>
            </div>

          </div>
        </div>
      </section>

      </main>

      <Footer />

      <Suspense fallback={null}>
        <AIChatbot />
      </Suspense>
    </div>
  );
}
